/**
 * Creates lifecycle helpers for settling and finalizing per-series downloads.
 * @param {Object} deps
 * @returns {{maybeFinalizeSeries: Function, settleBatchItemsWithoutParse: Function, settleSeriesItemWithoutParse: Function}}
 */
const createSeriesLifecycle = ({
  store,
  downloadPerf,
  dm,
  getSeriesLabel,
  canFinalizeSeriesDownload,
  setSeriesItemInFlight,
  setSeriesItemParseQueued,
  decrementInFlightDownloads,
  countPendingChunksForSeries,
  countParseQueueForSeries,
  seriesStateTrackers,
  clearSeriesRuntimeState
}) => {
  /**
   * Emits UI completion signals and clears per-series runtime state.
   * @param {string|number} seriesId
   * @returns {void}
   */
  const completeSeriesDownload = seriesId => {
    store.commit("viewer/downloadEnded", { seriesId });
    store.commit("viewer/setDownloadProgress", {
      seriesId,
      value: null
    });
    clearSeriesRuntimeState(seriesId);
    const seriesLabel = getSeriesLabel(seriesId);
    store.dispatch("raiseSnackbar", {
      text: seriesLabel
        ? `Download complete: ${seriesLabel} — advanced views now available`
        : "Series download complete — advanced views now available",
      color: "primary",
      timeout: 3000
    });
    dm.removeSeries(seriesId);
    downloadPerf.markSeriesComplete({ seriesId });
  };

  /**
   * Verifies finalization guards and completes the series when all are satisfied.
   * @param {string|number} seriesId
   * @param {string} [reason]
   * @returns {boolean}
   */
  const maybeFinalizeSeries = (seriesId, reason = "unspecified") => {
    const tracker = seriesStateTrackers[seriesId];
    if (!tracker || tracker.completed) {
      return false;
    }

    const { canFinalize, reason: blockReason, snapshot } =
      canFinalizeSeriesDownload(tracker, {
        pendingChunkCount: countPendingChunksForSeries(seriesId),
        parseQueueLength: countParseQueueForSeries(seriesId)
      });

    downloadPerf.markCompletionCheck({
      seriesId,
      reason,
      canFinalize,
      blocker: blockReason,
      declaredCount: snapshot.declaredCount,
      renderableCount: snapshot.renderableCount,
      inFlightItemIds: snapshot.inFlightIds,
      parseQueuedItemIds: snapshot.parseQueuedIds,
      pendingChunkCount: snapshot.pendingChunkCount,
      parseQueueLength: snapshot.parseQueueLength,
      lastDeclaredItemId: snapshot.lastDeclaredItemId,
      lastDeclaredItemState: snapshot.lastDeclaredItemState,
      unresolvedItemIds: snapshot.unresolvedIds
    });

    if (!canFinalize) {
      return false;
    }

    tracker.completed = true;
    completeSeriesDownload(seriesId);
    return true;
  };

  /**
   * Settles one item when parsing is skipped and triggers completion checks.
   * @param {string|number} seriesId
   * @param {string|number} dbImageId
   * @param {string} [reason]
   * @returns {void}
   */
  const settleSeriesItemWithoutParse = (
    seriesId,
    dbImageId,
    reason = "settled_without_parse"
  ) => {
    setSeriesItemInFlight(seriesStateTrackers[seriesId], dbImageId, false);
    setSeriesItemParseQueued(seriesStateTrackers[seriesId], dbImageId, false);
    dm.clearInFlight(seriesId, dbImageId);
    decrementInFlightDownloads();
    maybeFinalizeSeries(seriesId, reason);
  };

  /**
   * Settles all payload items without parse and returns settled count.
   * @param {Array} items
   * @param {string} reason
   * @returns {number}
   */
  const settleBatchItemsWithoutParse = (items, reason) => {
    if (!Array.isArray(items) || !items.length) {
      return 0;
    }
    items.forEach(item => {
      settleSeriesItemWithoutParse(item?.series_id, item?.db_id, reason);
    });
    return items.length;
  };

  return {
    maybeFinalizeSeries,
    settleBatchItemsWithoutParse,
    settleSeriesItemWithoutParse
  };
};

export { createSeriesLifecycle };
